import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Badge, Card, Page, PageStatus } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import api from '~/api';
import { DataListCard, DataListEntry } from '~/components/DataList';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import { usePathParams } from '~/hooks/usePathParams';
import { roleCodeLabel } from '~/utils/roleCodeLabel';

type UserWithAllRoles = CommandAdminApi.ResponseBody<'/user/:userId', 'get'>;

type StoreUserRole = UserWithAllRoles['storeUserRoles'][number];
type OrganizationUserRole = UserWithAllRoles['organizationUserRoles'][number];

const { idColumn } = sharedTableDefinitions;

function StoreUserRoles({
  sur,
  userId,
}: {
  sur: StoreUserRole[];
  userId: number;
}) {
  const navigate = useNavigate();
  const surColumns: ColumnDef<StoreUserRole>[] = [
    { accessorKey: 'storeId', header: 'Store ID' },
    { accessorKey: 'storeName', header: 'Name' },
    {
      accessorKey: 'roleCode',
      header: 'Role',
      cell: ({ row }) => <Badge>{roleCodeLabel[row.original.roleCode]}</Badge>,
    },
    idColumn<StoreUserRole>(),
  ];

  return (
    <Card>
      <Card.Heading>Store Access</Card.Heading>
      {match(sur)
        .with([], () => (
          <DataTableStatus
            icon="store"
            title="No Stores"
            details="This user has not be granted access to any stores."
          />
        ))
        .otherwise(() => (
          <DataTable
            columns={surColumns}
            data={sur}
            onRowClick={(store) =>
              navigate({
                pathname: `/stores/${store.storeId}`,
                search: new URLSearchParams({
                  returnTo: `/users/${userId}`,
                }).toString(),
              })
            }
          />
        ))}
    </Card>
  );
}

function OrganizationUserRoles({
  our,
  userId,
}: {
  our: OrganizationUserRole[];
  userId: number;
}) {
  const navigate = useNavigate();
  const ourColumns: ColumnDef<OrganizationUserRole>[] = [
    { accessorKey: 'organizationId', header: 'Organization ID' },
    { accessorKey: 'organizationName', header: 'Name' },
    {
      accessorKey: 'roleCode',
      header: 'Role',
      cell: ({ row }) => <Badge>{roleCodeLabel[row.original.roleCode]}</Badge>,
    },
    idColumn<OrganizationUserRole>(),
  ];

  return (
    <Card>
      <Card.Heading>Organization Access</Card.Heading>
      {match(our)
        .with([], () => (
          <DataTableStatus
            icon="organization"
            title="No Organizations"
            details="This user 
          has not been granted access to any organizations"
          />
        ))
        .otherwise(() => (
          <DataTable
            columns={ourColumns}
            data={our}
            onRowClick={(org) =>
              navigate({
                pathname: `/organizations/${org.organizationId}`,
                search: new URLSearchParams({
                  returnTo: `/users/${userId}`,
                }).toString(),
              })
            }
          />
        ))}
    </Card>
  );
}

export default function UserDetailPage() {
  const { userId } = usePathParams(
    z.object({ userId: z.coerce.number().optional() }),
  );

  const query = useQuery({
    queryKey: ['users', userId],
    queryFn: () => api.users.getById(userId),
    enabled: !!userId,
  });

  const items = [
    { label: 'Email', value: query.data?.email ?? '' },
    { label: 'First Name', value: query.data?.firstName ?? '' },
    { label: 'Last Name', value: query.data?.lastName ?? '' },
    { label: 'Created On', value: query.data?.createdOn ?? '' },
  ] satisfies DataListEntry[];

  return (
    <Page
      size="fullWidth"
      title="User"
      backAction={{
        to: '/users',
        accessibilityLabel: 'Back to users',
      }}
    >
      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading user details" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error
            title="Failed to load user details"
            error={query.error}
          />
        ))
        .with({ status: 'success', data: P.select() }, (user) => (
          <div className="flex flex-col gap-4">
            <DataListCard items={items} />
            <StoreUserRoles sur={user.storeUserRoles} userId={user.id} />
            <OrganizationUserRoles
              our={user.organizationUserRoles}
              userId={user.id}
            />
          </div>
        ))
        .exhaustive()}
    </Page>
  );
}
